import { useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type {
  AppConfiguration,
  ConfigurationPatch,
  ConfigurationProperty,
  ConfigurationValue,
  ConfigurationValues,
  Problem,
} from '../api/types';
import {
  coerceFieldValue,
  groupIssuesByKey,
  issuesFromProblem,
  problemMetaText,
  validateSubset,
  type FieldIssue,
} from './schema';

export interface ConfigFormProps {
  configuration: AppConfiguration;
  /** provisioning/updating/degradedの間は編集不可。 */
  editable: boolean;
  submitting: boolean;
  problem: Problem | null;
  onSubmit(patch: ConfigurationPatch): void;
}

/** widgetが保持する生の入力値。checkboxはboolean、selectはenumのindex文字列、他は文字列。 */
type WidgetValue = string | boolean;

/**
 * requiredなのに有効値が無いキー。明示値もdefaultも無い状態。
 * 基盤は登録も保存も止めず、app詳細と設定タブで警告するだけにしている。
 */
export function missingRequiredConfig(configuration: AppConfiguration): string[] {
  return (configuration.schema.required ?? []).filter(
    (key) => configuration.effectiveValues[key] === undefined,
  );
}

export function ConfigForm(props: ConfigFormProps): ReactElement {
  const { configuration, editable, submitting, problem, onSubmit } = props;
  const [edited, setEdited] = useState<Record<string, WidgetValue>>({});
  const [resetKeys, setResetKeys] = useState<string[]>([]);
  const [precheckIssues, setPrecheckIssues] = useState<FieldIssue[]>([]);
  const idPrefix = useId();

  const serverIssues = problem !== null && problem.status === 422 ? issuesFromProblem(problem) : [];
  const issues = groupIssuesByKey([...precheckIssues, ...serverIssues]);
  const disabled = !editable || submitting;
  const missing = missingRequiredConfig(configuration);

  function editField(key: string, next: WidgetValue): void {
    setEdited((prev) => ({ ...prev, [key]: next }));
    // 同じキーをsetとunsetへ同時に積まないため、編集したらunset対象から外す。
    setResetKeys((prev) => prev.filter((entry) => entry !== key));
  }

  function resetField(key: string): void {
    setResetKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setEdited((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const patch = buildPatch(configuration, edited, resetKeys);
    // requiredの未設定は保存を止めない（サーバーも拒否しない）。値そのものの不正だけを事前に見る。
    const found = validateSubset(
      { ...configuration.schema, required: [] },
      projectValues(configuration, patch),
    );
    setPrecheckIssues(found);
    if (found.length === 0) onSubmit(patch);
  }

  return (
    <section className="config-form">
      <p className="form-note">
        非秘密の値だけを保存します。保存内容の検証はサーバー側が正本で、この画面の検査は入力補助です。
      </p>
      <p className="form-note">
        「clientへ公開」はShellがiframeへ渡す値の制御であり、秘密の保護ではありません。
      </p>

      <form className="form" onSubmit={handleSubmit} noValidate>
        {Object.entries(configuration.schema.properties).map(([key, property]) => (
          <ConfigFieldRow
            key={key}
            id={`${idPrefix}-${key}`}
            fieldKey={key}
            property={property}
            value={displayedValue(configuration, edited, resetKeys, key, property)}
            hasExplicitValue={key in configuration.values && !resetKeys.includes(key)}
            missingRequired={missing.includes(key)}
            messages={issues.get(key) ?? []}
            disabled={disabled}
            onChange={(next) => editField(key, next)}
            onReset={() => resetField(key)}
          />
        ))}

        <ConfigProblemNotice
          problem={problem}
          expectedRevision={configuration.revision}
          unkeyedMessages={issues.get('') ?? []}
        />

        <button type="submit" className="form-submit" disabled={disabled}>
          {submitting ? '保存中…' : '設定を保存'}
        </button>
      </form>
    </section>
  );
}

interface ConfigFieldRowProps {
  id: string;
  fieldKey: string;
  property: ConfigurationProperty;
  value: WidgetValue;
  /** trueなら明示設定、falseならdefault由来または未設定。 */
  hasExplicitValue: boolean;
  /** requiredなのに明示値もdefaultも無い。 */
  missingRequired: boolean;
  messages: string[];
  disabled: boolean;
  onChange(next: WidgetValue): void;
  onReset(): void;
}

function ConfigFieldRow({
  id,
  fieldKey,
  property,
  value,
  hasExplicitValue,
  missingRequired,
  messages,
  disabled,
  onChange,
  onReset,
}: ConfigFieldRowProps): ReactElement {
  const errorId = `${id}-error`;
  const invalid = messages.length > 0;
  const control = {
    id,
    disabled,
    className: 'field-input',
    'aria-invalid': invalid,
    'aria-describedby': invalid ? errorId : undefined,
  };

  return (
    <div className={invalid ? 'field field-invalid' : 'field'}>
      <label className="field-label" htmlFor={id}>
        {fieldKey}
      </label>
      {hasExplicitValue ? (
        <span className="badge badge-explicit">明示設定</span>
      ) : missingRequired ? (
        <span className="badge badge-missing">未設定（必須）</span>
      ) : (
        <span className="badge badge-default">既定値</span>
      )}
      {property['x-client-visible'] === true && (
        <span className="badge badge-client-visible">clientへ公開</span>
      )}
      <p className="field-description">{property.description}</p>
      {renderWidget(property, value, control, onChange)}
      {hasExplicitValue && (
        <button type="button" className="field-reset" disabled={disabled} onClick={onReset}>
          既定に戻す
        </button>
      )}
      {messages.map((message) => (
        <p className="field-error" id={errorId} key={message}>
          {message}
        </p>
      ))}
    </div>
  );
}

interface ControlAttributes {
  id: string;
  disabled: boolean;
  className: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
}

function renderWidget(
  property: ConfigurationProperty,
  value: WidgetValue,
  control: ControlAttributes,
  onChange: (next: WidgetValue) => void,
): ReactElement {
  const choices = property.enum;
  if (choices !== undefined) {
    return (
      <select {...control} value={String(value)} onChange={(event) => onChange(event.target.value)}>
        {/* enum値はJSON値そのままで、DOMのvalue属性は文字列しか持てない。indexで対応付ける。 */}
        {value === '' && <option value="">（未選択）</option>}
        {choices.map((choice, index) => (
          <option key={String(choice)} value={String(index)}>
            {String(choice)}
          </option>
        ))}
      </select>
    );
  }
  if (property.type === 'boolean') {
    return (
      <input
        {...control}
        type="checkbox"
        checked={value === true}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }
  if (property.type === 'integer' || property.type === 'number') {
    return (
      <input
        {...control}
        type="number"
        value={String(value)}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <input
      {...control}
      type="text"
      value={String(value)}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** 表示中の値。編集値が最優先、既定に戻す指定中はdefault、それ以外は現在の有効値。 */
function displayedValue(
  configuration: AppConfiguration,
  edited: Record<string, WidgetValue>,
  resetKeys: string[],
  key: string,
  property: ConfigurationProperty,
): WidgetValue {
  const editedValue = edited[key];
  if (editedValue !== undefined) return editedValue;
  const value = resetKeys.includes(key) ? property.default : configuration.effectiveValues[key];
  return toWidgetValue(value, property);
}

function toWidgetValue(
  value: ConfigurationValue | undefined,
  property: ConfigurationProperty,
): WidgetValue {
  const choices = property.enum;
  if (choices !== undefined) {
    const index = value === undefined ? -1 : choices.indexOf(value);
    return index < 0 ? '' : String(index);
  }
  if (property.type === 'boolean') return value === true;
  return value === undefined ? '' : String(value);
}

/**
 * 編集した項目だけをsetへ、既定に戻す指定をunsetへ積む。両方に同じキーは入らない。
 * 空文字は未入力として扱いsetへ積まない。明示値を外すのは「既定に戻す」の役割。
 */
function buildPatch(
  configuration: AppConfiguration,
  edited: Record<string, WidgetValue>,
  resetKeys: string[],
): ConfigurationPatch {
  const set: ConfigurationValues = {};
  for (const [key, raw] of Object.entries(edited)) {
    const property = configuration.schema.properties[key];
    if (property === undefined) continue;
    const value = toConfigurationValue(raw, property);
    if (value !== undefined) set[key] = value;
  }
  return { expectedRevision: configuration.revision, set, unset: [...resetKeys] };
}

function toConfigurationValue(
  raw: WidgetValue,
  property: ConfigurationProperty,
): ConfigurationValue | undefined {
  const choices = property.enum;
  if (choices !== undefined) {
    const index = typeof raw === 'string' && raw !== '' ? Number(raw) : -1;
    return choices[index];
  }
  // 設定値はstring/integer/number/booleanに限られる契約なので、coerceFieldValueの戻りを
  // そのまま設定値として扱う。数値にならない入力は文字列のまま渡り、事前検査がtype違反を出す。
  return coerceFieldValue(raw, property.type) as ConfigurationValue | undefined;
}

/** patch適用後の有効値。サーバーと同じ「明示値→default→未設定」の順で組み、値の不正を検査する。 */
function projectValues(
  configuration: AppConfiguration,
  patch: ConfigurationPatch,
): ConfigurationValues {
  const projected: ConfigurationValues = { ...configuration.effectiveValues };
  for (const key of patch.unset) {
    const fallback = configuration.schema.properties[key]?.default;
    if (fallback === undefined) delete projected[key];
    else projected[key] = fallback;
  }
  return { ...projected, ...patch.set };
}

interface ConfigProblemNoticeProps {
  problem: Problem | null;
  expectedRevision: number;
  unkeyedMessages: string[];
}

function ConfigProblemNotice({
  problem,
  expectedRevision,
  unkeyedMessages,
}: ConfigProblemNoticeProps): ReactElement | null {
  if (problem === null) return null;

  if (problem.status === 409) {
    return (
      <div className="form-conflict" role="alert">
        <p>
          設定が別の経路で更新されています。この画面はrevision {expectedRevision}
          を前提にしています。最新を読み込んでからやり直してください。
        </p>
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  if (problem.status === 503) {
    // 配備中の一時的な拒否は利用者の入力誤りではないので、失敗としては見せない。
    return (
      <div className="form-notice" role="status">
        <p>配備中のため一時的に受け付けていません。完了後にもう一度保存してください。</p>
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  if (problem.status === 422) {
    return (
      <div className="form-error" role="alert">
        <p>設定値がサーバー検証で拒否されました。</p>
        {unkeyedMessages.map((message) => (
          <p className="form-error-detail" key={message}>
            {message}
          </p>
        ))}
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  return (
    <div className="form-error" role="alert">
      <p>{problem.title}</p>
      <p className="form-error-detail">{problem.detail}</p>
      <p className="form-error-meta">{problemMetaText(problem)}</p>
    </div>
  );
}
